// fieldDraft.js

import { normalizeCalendarColor } from 'utilities/CalendarFieldColor';
import {
    normalizeCurrencyCode,
    normalizeCurrencyCodeFieldValue,
    formatCurrencyDollarsString,
    parseCurrencyInputToCents,
    DEFAULT_CURRENCY_CODE,
} from 'utilities/Currency';
import { normalizeEmailValueForStorage } from 'utilities/Email';
import {
  applyChecklistTemplateFieldProps,
  normalizeAllowCustomChecklistItems,
  normalizeChecklistTemplateItems,
} from 'utilities/ChecklistTemplateItems';
import { normalizeEnumOptions, normalizeEnumOptionsSortMode } from 'utilities/EnumOptions';
import {
    canonicalizeSchemaField,
    isEnumCompatibleFieldType,
    isOptionsListFieldType,
    normalizeSchemaFieldType,
} from 'utilities/FieldTypes';
import { normalizeMeasurementDefaultUnit, normalizeMeasurementOptions } from 'utilities/Measurement';
import { formatPhoneValueForInput, normalizePhoneValueForStorage } from 'utilities/Phone';
import { normalizeRelationDisplayFields } from 'utilities/RelationDisplayFields';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';

import { DEFAULT_LABELS_BY_TYPE, ENUM_OPTIONS_SORT_MODE_MANUAL } from './constants';

export const normalizeOptionsForType = (options, type) => {
    if (isOptionsListFieldType(type)) return normalizeEnumOptions(options);
    if (type === 'measurement') return normalizeMeasurementOptions(options);
    return options;
};

const isEnumLikeType = (type) => isOptionsListFieldType(type);

const isOptionsDrivenType = (type) => isEnumLikeType(type) || type === 'measurement';

const normalizePercentage = (rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return '';
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return Math.round(parsed);
};

const normalizeSnapTo = (rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return 5;
    const rounded = Math.round(parsed);
    return rounded > 0 ? rounded : 5;
};

export const patchDraftWithRules = ({ prevDraft, patch, isEditing }) => {
    const next = { ...prevDraft, ...patch };
    const isNextMeasurementType = next.type === 'measurement';
    const isNextOptionsType = isOptionsDrivenType(next.type);

    if (patch.type) {
        const nextType = patch.type;
        const nextTypeIsEnumLike = isEnumLikeType(nextType);

        if (!isOptionsDrivenType(nextType)) {
            next.options = [];
            next.enumOptionsSort = undefined;
        } else {
            next.options = normalizeOptionsForType(next.options, nextType);
            next.enumOptionsSort = nextTypeIsEnumLike && isEnumCompatibleFieldType(nextType)
                ? normalizeEnumOptionsSortMode(next.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
                : undefined;
        }

        if (nextType === 'boolean' && typeof next.default !== 'boolean') next.default = false;
        if (nextType !== 'boolean' && typeof next.default === 'boolean') next.default = '';

        if (nextType === 'tags' || nextType === 'people' || nextType === 'checklist' || nextType === 'multiselect') {
            next.default = undefined;
        } else if (isEnumCompatibleFieldType(nextType) && Array.isArray(next.default)) {
            next.default = undefined;
        } else if (nextType === 'measurement') {
            next.default = undefined;
        }

        if (nextType !== 'relation') {
            next.targetType = '';
            next.allowMultiple = true;
            next.showFilter = false;
            next.displayFields = [];
        } else {
            if (typeof next.allowMultiple !== 'boolean') {
                next.allowMultiple = true;
            }
            if (typeof next.showFilter !== 'boolean') {
                next.showFilter = false;
            }
            if (next.allowMultiple !== false) {
                next.showFilter = false;
            }
            next.displayFields = normalizeRelationDisplayFields(next.displayFields);
        }

        if (nextType === 'people') {
            if (typeof next.showAccess !== 'boolean') next.showAccess = false;
            if (typeof next.showAsProfileStack !== 'boolean') next.showAsProfileStack = false;
        } else {
            next.showAccess = undefined;
            next.showAsProfileStack = undefined;
        }

        if (nextType === 'checklist') {
            next.checklistItems = normalizeChecklistTemplateItems(next.checklistItems);
            next.allowCustomItems = normalizeAllowCustomChecklistItems(next.allowCustomItems);
        } else {
            next.checklistItems = [];
            next.allowCustomItems = false;
        }

        if (nextType === 'percentage') {
            next.snapTo = normalizeSnapTo(next.snapTo);
        } else {
            next.snapTo = undefined;
        }

        if (nextType === 'money') {
            next.currencyCode = normalizeCurrencyCode(next.currencyCode);
        } else {
            next.currencyCode = undefined;
        }

        if (nextType === 'currency') {
            const code = normalizeCurrencyCodeFieldValue(next.default);
            next.default = code || DEFAULT_CURRENCY_CODE;
        }

        if (nextType === 'measurement') {
            next.defaultUnit = normalizeMeasurementDefaultUnit(next.defaultUnit, next.options);
        } else {
            next.defaultUnit = '';
        }

        next.calendarColor = isTemporalFieldType(nextType)
            ? normalizeCalendarColor(next.calendarColor)
            : '';

        if (!isEditing) {
            const currentLabel = typeof next.label === 'string' ? next.label.trim() : '';
            const defaultLabel = DEFAULT_LABELS_BY_TYPE[nextType];
            if (!currentLabel && defaultLabel) {
                next.label = defaultLabel;
            }
        }
    }

    if (
        next.type === 'relation'
        && Object.prototype.hasOwnProperty.call(patch, 'allowMultiple')
        && next.allowMultiple !== false
    ) {
        next.showFilter = false;
    }

    if (
        next.type === 'relation'
        && Object.prototype.hasOwnProperty.call(patch, 'targetType')
    ) {
        // Target type change invalidates prior display-field picks.
        next.displayFields = [];
    }

    if (
        next.type === 'relation'
        && Object.prototype.hasOwnProperty.call(patch, 'displayFields')
    ) {
        next.displayFields = normalizeRelationDisplayFields(patch.displayFields);
    }

    if (isNextOptionsType) {
        next.options = normalizeOptionsForType(next.options, next.type);
        next.enumOptionsSort = isEnumCompatibleFieldType(next.type)
            ? normalizeEnumOptionsSortMode(next.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
            : undefined;
    } else if (Object.prototype.hasOwnProperty.call(next, 'enumOptionsSort')) {
        next.enumOptionsSort = undefined;
    }

    if (isNextMeasurementType) {
        next.defaultUnit = normalizeMeasurementDefaultUnit(next.defaultUnit, next.options);
    } else if (Object.prototype.hasOwnProperty.call(next, 'defaultUnit')) {
        next.defaultUnit = '';
    }

    if (next.type === 'percentage' && patch.snapTo !== undefined) {
        next.snapTo = normalizeSnapTo(patch.snapTo);
    }

    if (next.type === 'money' && patch.currencyCode !== undefined) {
        next.currencyCode = normalizeCurrencyCode(patch.currencyCode);
    }

    if (next.type === 'checklist') {
        if (Object.prototype.hasOwnProperty.call(patch, 'checklistItems')) {
            next.checklistItems = normalizeChecklistTemplateItems(patch.checklistItems);
        } else {
            next.checklistItems = normalizeChecklistTemplateItems(next.checklistItems);
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'allowCustomItems')) {
            next.allowCustomItems = normalizeAllowCustomChecklistItems(patch.allowCustomItems);
        } else {
            next.allowCustomItems = normalizeAllowCustomChecklistItems(next.allowCustomItems);
        }
    } else {
        next.checklistItems = [];
        next.allowCustomItems = false;
    }

    return next;
};

export const buildDraftFromField = (field) => {
    const canonical = canonicalizeSchemaField(field) || field;
    const type = normalizeSchemaFieldType(canonical?.type, 'string') || 'string';
    const options = normalizeOptionsForType(canonical?.options || [], type);
    const rawDefault = canonical?.default;

    let nextDefault = rawDefault;
    if (type === 'boolean') {
        nextDefault = !!rawDefault;
    } else if (type === 'number' || type === 'percentage') {
        nextDefault = rawDefault === undefined || rawDefault === null ? '' : rawDefault;
    } else if (type === 'money') {
        nextDefault = rawDefault === undefined || rawDefault === null || rawDefault === ''
            ? ''
            : formatCurrencyDollarsString(rawDefault, normalizeCurrencyCode(canonical?.currencyCode || canonical?.currency));
    } else if (type === 'currency') {
        nextDefault = normalizeCurrencyCodeFieldValue(rawDefault) || DEFAULT_CURRENCY_CODE;
    } else if (isEnumCompatibleFieldType(type)) {
        if (rawDefault === undefined || rawDefault === null) {
            nextDefault = '';
        } else if (Array.isArray(rawDefault)) {
            nextDefault = '';
        } else {
            nextDefault = String(rawDefault);
        }
    } else if (type === 'multiselect') {
        nextDefault = undefined;
    } else if (type === 'phone') {
        nextDefault = rawDefault === undefined || rawDefault === null
            ? ''
            : formatPhoneValueForInput(rawDefault);
    } else if (type === 'email') {
        nextDefault = rawDefault === undefined || rawDefault === null
            ? ''
            : normalizeEmailValueForStorage(rawDefault);
    } else if (type === 'tags' || type === 'people' || type === 'checklist') {
        nextDefault = undefined;
    } else {
        nextDefault = rawDefault === undefined || rawDefault === null ? '' : rawDefault;
    }

    return {
        label: canonical?.label || '',
        fieldDescription: String(canonical?.fieldDescription || '').trim(),
        type,
        targetType: type === 'relation' ? String(canonical?.targetType || '').trim() : '',
        allowMultiple: type === 'relation' ? canonical?.allowMultiple !== false : true,
        showFilter: type === 'relation'
            && canonical?.allowMultiple === false
            && canonical?.showFilter === true,
        displayFields: type === 'relation'
            ? normalizeRelationDisplayFields(canonical?.displayFields)
            : [],
        showAccess: type === 'people' ? canonical?.showAccess === true : undefined,
        showAsProfileStack: type === 'people' ? canonical?.showAsProfileStack === true : undefined,
        includeInQrPrint: canonical?.includeInQrPrint === true,
        displayInDefaultLists: canonical?.displayInDefaultLists === true,
        checklistItems: type === 'checklist'
            ? normalizeChecklistTemplateItems(canonical?.checklistItems)
            : [],
        allowCustomItems: type === 'checklist'
            ? normalizeAllowCustomChecklistItems(canonical?.allowCustomItems)
            : false,
        default: nextDefault,
        defaultUnit: type === 'measurement'
            ? normalizeMeasurementDefaultUnit(canonical?.defaultUnit, options)
            : '',
        options,
        enumOptionsSort: isEnumCompatibleFieldType(type)
            ? normalizeEnumOptionsSortMode(canonical?.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL)
            : undefined,
        snapTo: type === 'percentage' ? normalizeSnapTo(canonical?.snapTo) : undefined,
        currencyCode: type === 'money'
            ? normalizeCurrencyCode(canonical?.currencyCode || canonical?.currency)
            : undefined,
        calendarColor: isTemporalFieldType(type) ? normalizeCalendarColor(canonical?.calendarColor) : '',
    };
};

export const buildNextFieldFromDraft = (draft) => {
    const isEnumType = isEnumCompatibleFieldType(draft.type);
    const isMultiSelectType = draft.type === 'multiselect';
    const isTagsType = draft.type === 'tags';
    const isPeopleType = draft.type === 'people';
    const isChecklistType = draft.type === 'checklist';
    const isMeasurementType = draft.type === 'measurement';
    const normalizedMeasurementOptions = isMeasurementType
        ? normalizeMeasurementOptions(draft.options)
        : undefined;

    const nextField = {
        label: draft.label.trim(),
        fieldDescription: String(draft.fieldDescription || '').trim(),
        type: draft.type,
        targetType: draft.type === 'relation' ? String(draft.targetType || '').trim() : undefined,
        allowMultiple: draft.type === 'relation' ? draft.allowMultiple !== false : undefined,
        showFilter: draft.type === 'relation'
            && draft.allowMultiple === false
            && draft.showFilter === true,
        displayFields: draft.type === 'relation'
            ? normalizeRelationDisplayFields(draft.displayFields)
            : undefined,
        showAccess: draft.type === 'people' ? draft.showAccess === true : undefined,
        showAsProfileStack: draft.type === 'people' ? draft.showAsProfileStack === true : undefined,
        includeInQrPrint: draft.includeInQrPrint === true ? true : undefined,
        displayInDefaultLists: draft.displayInDefaultLists === true ? true : undefined,
        options: isEnumType || isMultiSelectType
            ? normalizeEnumOptions(draft.options)
            : normalizedMeasurementOptions,
        default: draft.default,
        defaultUnit: isMeasurementType
            ? normalizeMeasurementDefaultUnit(draft.defaultUnit, normalizedMeasurementOptions)
            : undefined,
    };

    if (draft.type === 'number') {
        const raw = typeof draft.default === 'string' ? draft.default.trim() : '';
        nextField.default = raw === '' ? '' : Number(raw);
    }

    if (draft.type === 'percentage') {
        const raw = typeof draft.default === 'string' ? draft.default.trim() : draft.default;
        if (raw === '') {
            nextField.default = '';
        } else {
            nextField.default = normalizePercentage(raw);
        }
        nextField.snapTo = normalizeSnapTo(draft.snapTo);
    }

    if (draft.type === 'money') {
        nextField.currencyCode = normalizeCurrencyCode(draft.currencyCode);
        const raw = typeof draft.default === 'string' ? draft.default.trim() : draft.default;
        if (raw === '' || raw === undefined || raw === null) {
            nextField.default = '';
        } else {
            const parsed = parseCurrencyInputToCents(String(raw), nextField.currencyCode);
            nextField.default = parsed.ok ? parsed.cents : '';
        }
    }

    if (draft.type === 'currency') {
        nextField.default = normalizeCurrencyCodeFieldValue(draft.default) || DEFAULT_CURRENCY_CODE;
        delete nextField.currencyCode;
    }

    if (isEnumCompatibleFieldType(draft.type)) {
        nextField.default = draft.default === '' ? undefined : draft.default;
        nextField.enumOptionsSort = normalizeEnumOptionsSortMode(draft.enumOptionsSort || ENUM_OPTIONS_SORT_MODE_MANUAL);
    } else {
        delete nextField.enumOptionsSort;
    }

    if (draft.type === 'boolean') {
        nextField.default = !!draft.default;
    }

    if (draft.type === 'phone') {
        nextField.default = normalizePhoneValueForStorage(draft.default);
    }

    if (draft.type === 'email') {
        nextField.default = normalizeEmailValueForStorage(draft.default);
    }

    if (isTagsType || isPeopleType || isChecklistType || isMultiSelectType || isMeasurementType) {
        nextField.default = undefined;
    }

    if (isChecklistType) {
        applyChecklistTemplateFieldProps(nextField, draft);
    } else {
        delete nextField.checklistItems;
        delete nextField.allowCustomItems;
        delete nextField.templateKeys;
    }

    if (!isMeasurementType) {
        delete nextField.defaultUnit;
    }

    if (draft.type !== 'money') {
        delete nextField.currencyCode;
    }

    if (!nextField.fieldDescription) {
        delete nextField.fieldDescription;
    }

    if (draft.type !== 'relation') {
        delete nextField.targetType;
        delete nextField.allowMultiple;
        delete nextField.showFilter;
        delete nextField.displayFields;
    } else if (nextField.allowMultiple !== false || !nextField.showFilter) {
        delete nextField.showFilter;
    }

    if (draft.type !== 'people') {
        delete nextField.showAccess;
        delete nextField.showAsProfileStack;
    } else {
        if (!nextField.showAccess) {
            delete nextField.showAccess;
        }
        if (!nextField.showAsProfileStack) {
            delete nextField.showAsProfileStack;
        }
    }

    if (isTemporalFieldType(draft.type)) {
        nextField.calendarColor = normalizeCalendarColor(draft.calendarColor);
    }

    if (!nextField.includeInQrPrint) {
        delete nextField.includeInQrPrint;
    }

    if (!nextField.displayInDefaultLists) {
        delete nextField.displayInDefaultLists;
    }

    return nextField;
};
